import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { Types } from '@notifi-network/notifi-graphql';
import {
  EventTypeItem,
  NotifiFrontendClient,
  UserState,
} from '@notifi-network/notifi-frontend-client';
import { SupportedCardConfigType } from '@notifi-network/notifi-frontend-client';
import { MESSAGES_PER_PAGE } from '../utils/constants';

export type SubscriptionData = Readonly<{
  alerts: Readonly<Record<string, Types.AlertFragmentFragment>>;
  email: string | null;
  phoneNumber: string | null;
  telegramId: string | null;
  telegramConfirmationUrl: string | null;
  isPhoneNumberConfirmed: boolean | null;
  emailIdThatNeedsConfirmation: string;
}>;

export type ClientDataProps = {
  alerts: ReadonlyArray<Types.AlertFragmentFragment>;
  notifications: ReadonlyArray<Types.NotificationHistoryEntryFragmentFragment>;
  sources: ReadonlyArray<Types.SourceFragmentFragment>;
  sourceGroups: ReadonlyArray<Types.SourceGroupFragmentFragment>;
  targetGroups: ReadonlyArray<Types.TargetGroupFragmentFragment>;
  emailTargets: ReadonlyArray<Types.EmailTargetFragmentFragment>;
  smsTargets: ReadonlyArray<Types.SmsTargetFragmentFragment>;
  telegramTargets: ReadonlyArray<Types.TelegramTargetFragmentFragment>;
};

export type ClientStateProps = {
  clientToken: number | null;
  tokenAddress: string | null;
  roles: Array<string>;
};

export type FetchedCardState = 'edit' | 'preview' | 'history';

export type LoadingState = Readonly<{
  state: 'loading';
}>;

export type FetchedState = Readonly<{
  state: 'fetched';
  data: SupportedCardConfigType;
}>;

export type ErrorState = Readonly<{
  state: 'error';
  reason: unknown;
}>;

export type SubscriptionCardState = LoadingState | FetchedState | ErrorState;
export type TargetFieldType = 'emailAddress' | 'phoneNumber' | 'telegramId';

export const useNotifiStore = defineStore('NotifiStore', () => {
  const myWalletAddress = ref<string | null>(null);
  const loading = ref<boolean>(false);
  const notifiClient = ref<NotifiFrontendClient | null>(null);
  const fetchedCardState = ref<FetchedCardState>('edit');
  const emailAddress = ref<string>('');
  const phoneNumber = ref<string>('');
  const telegramId = ref<string>('');
  const userState = ref<UserState | undefined>(undefined);
  const isFTU = ref<boolean>(false);
  const errorMessage = ref<string>('');

  const setmyWalletAddress = (walletAddr: string) => {
    myWalletAddress.value = walletAddr;
  };

  const emailIdThatNeedsConfirmation = ref<string>('');
  const telegramVerificationUrl = ref<string>('');

  const setSubscriptionCardState = (cardState: SubscriptionCardState) => {
    subscriptionCardState.value = cardState;
  };

  function setInputFieldState(formInput: {
    input: string;
    field: TargetFieldType;
  }) {
    const { input, field } = formInput;

    let selectedField;

    switch (field) {
      case 'phoneNumber':
        selectedField = phoneNumber;
        break;
      case 'emailAddress':
        selectedField = emailAddress;
        break;
      case 'telegramId':
        selectedField = telegramId;
        break;
    }
    if (selectedField) {
      selectedField.value = input;
    }
  }

  const subscriptionCardState = ref<SubscriptionCardState>({
    state: 'loading',
  });

  const clientData = ref<ClientDataProps>({
    alerts: [],
    notifications: [],
    sources: [],
    sourceGroups: [],
    targetGroups: [],
    emailTargets: [],
    smsTargets: [],
    telegramTargets: [],
  });

  const notificationsPageInfo = ref<Types.PageInfoFragmentFragment | undefined>(
    undefined,
  );

  const setCardState = (state: FetchedCardState) => {
    fetchedCardState.value = state;
  };

  const handleUpdatingTargetGroupState = (
    targetGroup: Types.TargetGroupFragmentFragment | undefined,
  ) => {
    const emailTarget = targetGroup?.emailTargets?.[0];

    const emailAddress = emailTarget?.emailAddress ?? '';
    setInputFieldState({
      input: emailAddress,
      field: 'emailAddress',
    });

    const emailIdThatNeedsConfirmation =
      emailTarget?.isConfirmed === false ? emailTarget.id : '';
    setEmailIdThatNeedsConfirmation(emailIdThatNeedsConfirmation);

    const telegramTarget = targetGroup?.telegramTargets?.[0];
    const telegramConfirmationUrl = telegramTarget?.confirmationUrl ?? '';
    const telegramUrlRegex = computed(() => {
      return new RegExp(
        /(https?:\/\/)?(www[.])?(telegram|t)\.me\/([a-zA-Z0-9_-]*)\/?\?(.*)$/,
      );
    });

    if (telegramUrlRegex.value.test(telegramConfirmationUrl)) {
      setTelegramVerificationUrl(telegramConfirmationUrl);
    }

    const telegramId = telegramTarget?.telegramId ?? '';

    setInputFieldState({
      input: telegramId,
      field: 'telegramId',
    });
    // TODO: telegram confirmation url

    const smsTarget = targetGroup?.smsTargets?.[0];
    const phoneNumber = smsTarget?.phoneNumber ?? '';

    setInputFieldState({
      input: phoneNumber,
      field: 'phoneNumber',
    });
  };

  const dedupNotifications = (
    notificationEntries: Types.NotificationHistoryEntryFragmentFragment[],
  ): Types.NotificationHistoryEntryFragmentFragment[] => {
    const uniqueEntries: string[] = [];

    const dedupedEntries = notificationEntries.filter((entry) => {
      const isDuplicate = uniqueEntries.includes(entry.id);

      if (!isDuplicate) {
        uniqueEntries.push(entry.id);

        return true;
      }

      return false;
    });

    return dedupedEntries;
  };
  const fetchNotificationsPage = async () => {
    const client = notifiClient.value;
    if (client === null || notificationsPageInfo.value?.hasNextPage === false) {
      return;
    }

    const results = await client.getNotificationHistory({
      after: notificationsPageInfo.value?.endCursor,
      first: MESSAGES_PER_PAGE,
    });

    const notificationsToHandle = [
      ...clientData.value.notifications,
      ...results.nodes,
    ];

    const dedupedNotifications = dedupNotifications(notificationsToHandle);

    clientData.value = {
      ...clientData.value,
      notifications: [...dedupedNotifications],
    };

    notificationsPageInfo.value = results.pageInfo;
  };

  const fetchAndHandleClientData = async (
    newClient: Omit<
      NotifiFrontendClient,
      | '_configuration'
      | '_service'
      | '_storage'
      | '_signMessage'
      | '_handleLogInResult'
      | '_updateTargetGroup'
    >,
  ) => {
    const [alerts, targetGroups] = await Promise.all([
      newClient.getAlerts(),
      newClient.getTargetGroups(),
      newClient.getSourceGroups(),
    ]);

    setAlerts([...alerts]);
    setTargetGroups([...targetGroups]);

    if (targetGroups.length === 0) {
      setIsFTU(true);
    }

    const defaultTargetGroup = targetGroups.find((tg) => tg.name === 'Default');
    handleUpdatingTargetGroupState(defaultTargetGroup);

    // Reset history before fetching
    setNotificationHistory([]);
    notificationsPageInfo.value = undefined;
    fetchNotificationsPage();

    if (defaultTargetGroup !== undefined) {
      setCardState('history');
    }
  };

  const handleNewClient = async (
    newClient: Omit<
      NotifiFrontendClient,
      | '_configuration'
      | '_service'
      | '_storage'
      | '_signMessage'
      | '_handleLogInResult'
      | '_updateTargetGroup'
    >,
  ) => {
    const newUserState = await newClient.initialize();
    userState.value = newUserState;

    if (newUserState.status === 'authenticated') {
      fetchAndHandleClientData(newClient);
    } else {
      setIsFTU(true);

      setCardState('edit');
    }
  };

  const setIsFTU = (value: boolean) => {
    isFTU.value = value;
  };

  const setNotifiClient = (
    client: Omit<
      NotifiFrontendClient,
      | '_configuration'
      | '_service'
      | '_storage'
      | '_signMessage'
      | '_handleLogInResult'
      | '_updateTargetGroup'
    >,
  ) => {
    notifiClient.value = client;
    loading.value = true;

    handleNewClient(client).finally(() => {
      loading.value = false;
    });
  };

  const setAlerts = (alerts: Types.AlertFragmentFragment[]) => {
    clientData.value = { ...clientData.value, alerts };
  };

  const setTargetGroups = (
    targetGroups: Types.TargetGroupFragmentFragment[],
  ) => {
    clientData.value = { ...clientData.value, targetGroups };
  };

  const setNotificationHistory = (
    notifications: Types.NotificationHistoryEntryFragmentFragment[],
  ) => {
    clientData.value = { ...clientData.value, notifications };
  };

  const setEmailIdThatNeedsConfirmation = (emailId: string) => {
    emailIdThatNeedsConfirmation.value = emailId;
  };

  const subscribe = async (
    eventType: EventTypeItem,
    inputs: Record<string, string | undefined>,
  ) => {
    if (notifiClient.value === null) {
      return;
    }
    loading.value = true;

    await notifiClient.value
      .ensureAlert({
        eventType,
        inputs,
      })
      .then((alert) => {
        const alerts = clientData.value.alerts;
        const idx = alerts.findIndex((it) => it?.id === alert.id);
        if (idx > 0) {
          clientData.value = {
            ...clientData.value,
            alerts: [...alerts.slice(0, idx), alert, ...alerts.slice(idx + 1)],
          };
        } else {
          clientData.value = {
            ...clientData.value,
            alerts: [...alerts, alert],
          };
        }
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const unsubscribe = async (name: string) => {
    if (notifiClient.value === null) {
      return;
    }

    const newAlerts = await notifiClient.value.getAlerts();

    const existing = newAlerts.find((it) => it?.name === name);
    if (existing === undefined) {
      clientData.value = { ...clientData.value, alerts: newAlerts };
      return;
    }

    loading.value = true;
    await notifiClient.value
      .deleteAlert({
        id: existing.id,
      })
      .then(() => {
        const alerts = newAlerts;
        const idx = alerts.findIndex((it) => it?.id === existing.id);
        if (idx < 0) {
          return;
        }
        clientData.value = {
          ...clientData.value,
          alerts: [...alerts.slice(0, idx), ...alerts.slice(idx + 1)],
        };
      })
      .finally(() => {
        loading.value = false;
      });
    loading.value = false;
  };

  const resendEmailVerificationLink = async () => {
    if (notifiClient.value === null) {
      return;
    }

    await notifiClient.value.sendEmailTargetVerification({
      targetId: emailIdThatNeedsConfirmation.value,
    });
  };

  const setTelegramVerificationUrl = (url: string) => {
    telegramVerificationUrl.value = url;
  };

  const setErrorMessage = (value: string) => {
    errorMessage.value = value;
  };

  watch(myWalletAddress, () => {
    setNotificationHistory([]);
    notificationsPageInfo.value = undefined;
    fetchNotificationsPage();
  });

  return {
    errorMessage,
    clientData,
    emailAddress,
    emailIdThatNeedsConfirmation,
    fetchedCardState,
    handleUpdatingTargetGroupState,
    isFTU,
    loading,
    fetchNotificationsPage,
    resendEmailVerificationLink,
    setAlerts,
    phoneNumber,
    notifiClient,
    setCardState,
    setEmailIdThatNeedsConfirmation,
    setIsFTU,
    setInputFieldState,
    setNotifiClient,
    setErrorMessage,
    setSubscriptionCardState,
    setTargetGroups,
    setTelegramVerificationUrl,
    setNotificationHistory,
    setmyWalletAddress,
    subscribe,
    subscriptionCardState,
    telegramId,
    telegramVerificationUrl,
    unsubscribe,
    userState,
    myWalletAddress,
  };
});
